import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  SKILL_PROFICIENCIES,
  UserSkillsNotPermittedError,
  useAssignUserSkill,
  useRemoveUserSkill,
  useSkills,
  useUpdateUserSkill,
  useUserSkills,
  type SkillProficiency,
  type UserSkill,
} from '@/routes/skills/useSkills';

/**
 * A person's skills on their profile page (M1-38).
 *
 * <b>Keyed on the USER id, not the profile id</b> — unlike its sibling
 * `TechnicianLicenseSection`, and deliberately so (api/CONVENTIONS.md's M1-38
 * section). The visible consequence is that this section renders on the
 * "no profile yet" branch of `TechnicianProfileDetail` too: a small shop's
 * Owner who also estimates has skills worth recording and will never have a
 * technician profile, and the real dev account contains exactly that person.
 *
 * <b>Read-only unless the viewer holds `users.manage`</b>, including for the
 * technician looking at their OWN skills — the same call M1-37 made for
 * licences and for the same reason: the AC hands the assignment to the office
 * ("owner/admin assign a skill + proficiency"), so there is no self-edit
 * affordance here at all rather than one that renders and then 403s. The API
 * is still the enforcement point; this only decides what to draw.
 */
export function TechnicianSkillSection({
  userId,
  canManage,
  onAnnounce,
}: {
  userId: string;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const held = useUserSkills(userId);
  // The catalog is `users.read`-gated and a Technician holds no user
  // permission, so it is only ever fetched for a viewer who can act on it.
  // Their own labels come from the assignments above, which is the whole
  // reason those carry a label at all.
  const catalog = useSkills(false, canManage);
  const remove = useRemoveUserSkill(userId);

  const [pendingRemove, setPendingRemove] = useState<UserSkill | null>(null);

  // A technician viewing someone else's profile: the API refuses, and that is
  // a real state rather than a load failure to retry.
  if (held.error instanceof UserSkillsNotPermittedError) {
    return null;
  }

  const assignable = (catalog.data ?? []).filter(
    (skill) => !held.data?.some((row) => row.skillId === skill.id),
  );

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('technicianSkills.heading')}</h2>
      </div>

      {held.isPending ? (
        <div className="mt-2">
          <LoadingState label={t('technicianSkills.loading')} />
        </div>
      ) : held.isError ? (
        <div className="mt-2">
          <ErrorState
            title={t('technicianSkills.loadErrorTitle')}
            description={t('technicianSkills.loadErrorDescription')}
            onRetry={() => void held.refetch()}
          />
        </div>
      ) : held.data.length === 0 ? (
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          {canManage ? t('technicianSkills.emptyCanManage') : t('technicianSkills.emptyReadOnly')}
        </p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {held.data.map((skill) => (
            <SkillRow
              key={skill.skillId}
              userId={userId}
              skill={skill}
              canManage={canManage}
              onRemove={() => {
                // reset() first: this mutation is hoisted to the section and
                // outlives each ConfirmDialog, so without it a failed removal
                // leaves isError true and the NEXT skill's dialog opens already
                // announcing a failure this user has not caused. The same trap
                // TechnicianLicenseSection documents.
                remove.reset();
                setPendingRemove(skill);
              }}
              onAnnounce={onAnnounce}
            />
          ))}
        </ul>
      )}

      {canManage ? (
        <AddSkillRow
          userId={userId}
          assignable={assignable}
          catalogIsPending={catalog.isPending}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {pendingRemove ? (
        <ConfirmDialog
          titleId="remove-skill-title"
          title={t('technicianSkills.remove.title')}
          description={t('technicianSkills.remove.description', { label: pendingRemove.skillLabel })}
          confirmLabel={t('technicianSkills.remove.confirm')}
          isPending={remove.isPending}
          errorMessage={remove.isError ? t('technicianSkills.remove.failed') : undefined}
          testId="remove-skill-dialog"
          onConfirm={() =>
            remove.mutate(pendingRemove.skillId, {
              onSuccess: () => {
                // The caller's caller does the t() call, never the callback
                // itself — web/CONVENTIONS.md's M1-30 note.
                onAnnounce(t('technicianSkills.remove.done'));
                setPendingRemove(null);
              },
            })
          }
          onClose={() => setPendingRemove(null)}
        />
      ) : null}
    </div>
  );
}

function SkillRow({
  userId,
  skill,
  canManage,
  onRemove,
  onAnnounce,
}: {
  userId: string;
  skill: UserSkill;
  canManage: boolean;
  onRemove: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateUserSkill(userId);
  const selectId = `skill-proficiency-${skill.skillId}`;

  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border p-3" data-testid={`skill-${skill.skillId}`}>
      <span className="font-medium">{skill.skillLabel}</span>

      {/* A retired catalog entry keeps its assignment and says so, rather than
          vanishing from the person it describes. Retiring means "stop offering
          this", not "nobody had it" — and without the chip a skill silently
          missing from the picker looks like a bug on this screen. */}
      {skill.skillIsRetired ? (
        <span className="inline-block rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted-foreground)]">
          {t('technicianSkills.retiredChip')}
        </span>
      ) : null}

      <div className="ms-auto flex items-center gap-2">
        {canManage ? (
          <>
            {/* Explicit htmlFor/id, never a wrapping <label>: a label wrapped
                around a <select> folds every option's text into the control's
                accessible name, which is the real screen-reader defect M1-37's
                own tests found the hard way. */}
            <label htmlFor={selectId} className="sr-only">
              {t('technicianSkills.proficiencyLabelFor', { label: skill.skillLabel })}
            </label>
            <select
              id={selectId}
              className={FIELD_CLASS}
              value={skill.proficiency}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate(
                  { skillId: skill.skillId, proficiency: event.target.value as SkillProficiency },
                  { onSuccess: () => onAnnounce(t('technicianSkills.proficiencyChanged')) },
                )
              }
            >
              {SKILL_PROFICIENCIES.map((level) => (
                <option key={level} value={level}>
                  {t(`technicianSkills.proficiency.${level}`)}
                </option>
              ))}
            </select>
            <Button type="button" variant="outline" size="sm" onClick={onRemove}>
              {t('technicianSkills.removeAction')}
            </Button>
          </>
        ) : (
          <span className="text-sm text-[var(--color-muted-foreground)]">
            {t(`technicianSkills.proficiency.${skill.proficiency}`)}
          </span>
        )}
      </div>
    </li>
  );
}

function AddSkillRow({
  userId,
  assignable,
  catalogIsPending,
  onAnnounce,
}: {
  userId: string;
  assignable: readonly { id: string; label: string }[];
  catalogIsPending: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const assign = useAssignUserSkill(userId);
  const [skillId, setSkillId] = useState('');
  const [proficiency, setProficiency] = useState<SkillProficiency>('Basic');

  if (catalogIsPending) {
    return null;
  }

  // Nothing left to add is a sentence, not a disabled dropdown with no
  // explanation — and it is a different sentence from "your catalog is empty",
  // which is the office's cue to go and fill one in.
  if (assignable.length === 0) {
    return (
      <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
        {t('technicianSkills.nothingLeftToAdd')}
      </p>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
      <div className="grid gap-1">
        <label htmlFor="add-skill-skill" className="text-sm font-medium">
          {t('technicianSkills.add.skillLabel')}
        </label>
        <select
          id="add-skill-skill"
          className={FIELD_CLASS}
          value={skillId}
          onChange={(event) => setSkillId(event.target.value)}
        >
          <option value="">{t('technicianSkills.add.choose')}</option>
          {assignable.map((skill) => (
            <option key={skill.id} value={skill.id}>
              {skill.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-1">
        <label htmlFor="add-skill-proficiency" className="text-sm font-medium">
          {t('technicianSkills.add.proficiencyLabel')}
        </label>
        <select
          id="add-skill-proficiency"
          className={FIELD_CLASS}
          value={proficiency}
          onChange={(event) => setProficiency(event.target.value as SkillProficiency)}
        >
          {SKILL_PROFICIENCIES.map((level) => (
            <option key={level} value={level}>
              {t(`technicianSkills.proficiency.${level}`)}
            </option>
          ))}
        </select>
      </div>

      <Button
        type="button"
        size="sm"
        disabled={skillId === '' || assign.isPending}
        onClick={() =>
          assign.mutate(
            { skillId, proficiency },
            {
              onSuccess: () => {
                onAnnounce(t('technicianSkills.add.done'));
                setSkillId('');
                setProficiency('Basic');
              },
            },
          )
        }
      >
        {t('technicianSkills.add.action')}
      </Button>

      {assign.isError ? (
        <p role="alert" className="w-full text-sm text-[var(--color-destructive)]">
          {t('technicianSkills.add.failed')}
        </p>
      ) : null}
    </div>
  );
}
